import React from 'react';

import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import YouTube from 'react-youtube';
import { css, styled } from 'styled-components';

import theme from 'styles/theme';

import { ArticleDate } from 'components/ArticleDate';
import InnerHtml from 'components/InnerHtml/InnerHtml';
import Link from 'components/Link';

import { ArticleI } from 'interfaces/content-stack/modular-blocks/watch';

import { buildBreadcrumbUrls } from 'utils/functions';

type Size = 'lg' | 'sm';

type WatchCardProps = {
  id: string;
  article: ArticleI;
  size?: Size;
};

const Title = styled('h3')<{ $large?: boolean }>(
  ({ theme, $large }) => css`
    font-family: ${theme.fonts.default};
    font-weight: 700;
    font-size: ${$large ? '1.25rem' : '1rem'};
    color: ${theme.colors.white};
    margin: 0;
  `
);

const LargeLink = styled(Link)(
  () => css`
    color: ${theme.colors.white};
    height: 100%;
    text-decoration: none;
    width: 100%;

    .excerpt {
      font-weight: 500;
    }
  `
);

const YouTubeWrapper = styled.div`
  width: 100%;

  .youtube-container {
    height: 0;
    padding-bottom: 56.25%;
    position: relative;

    // using !important here to ensure youtube defaults or anything inline is overriden
    iframe {
      height: 100% !important;
      left: 0;
      position: absolute;
      top: 0;
      width: 100% !important;
    }
  }
`;

const VideoContainer = styled('div')<{ $large?: boolean }>(
  ({ $large }) => css`
    display: flex;
    flex-direction: column;
    overflow: hidden;

    ${$large &&
    css`
      flex-grow: 1;
    `}
  `
);

const Arrow = styled(FontAwesomeIcon)`
  color: ${({ theme }) => theme.colors.ttRed};
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  font-size: 1.25rem;
`;

const TextContainer = styled.div`
  ${({ theme }) => `
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    align-items: flex-start;
    justify-content: flex-start;
    width: 100%;
    gap: 0.75rem;
    background-color: ${theme.colors.backgroundBlack};
    height: 100%;
    padding: 1.5rem 2rem 2rem 2rem;
    position: relative;

    &:hover {
      background-color: ${theme.colors.hoverBackgroundBlack};
    }

    @media (max-width: ${theme.breakpoints.lg}) {
      padding-left: 1rem;
      padding-right: 1rem;
    }

    @media (max-width: ${theme.breakpoints.md}) {
      padding-bottom: 2.5rem;
    }
  `}
`;

const WatchCard: React.FC<WatchCardProps> = ({ id, size = 'lg', article }) => {
  const { breadcrumb, postDate, title, url, metadata } = article;

  const large = size === 'lg';

  return (
    <VideoContainer $large={large}>
      <YouTubeWrapper>
        <YouTube className='youtube-container' videoId={id} />
      </YouTubeWrapper>
      <LargeLink to={buildBreadcrumbUrls(breadcrumb, url) ?? ''}>
        <TextContainer>
          <ArticleDate date={postDate} />
          <Title $large={large}>{title}</Title>
          {size === 'lg' && <InnerHtml className='excerpt' richText={metadata.excerpt} />}
          <Arrow icon={faArrowRight} />
        </TextContainer>
      </LargeLink>
    </VideoContainer>
  );
};

export default WatchCard;
